import { type ReactNode } from 'react'
import type { SectionConfig } from '../index'

export interface PresentationWrapperProps {
  /** Presentation title for metadata */
  title: string
  /** Section metadata for tracking */
  sections?: SectionConfig[]
  /** Presentation content */
  children: ReactNode
  /** Additional className for the wrapper */
  className?: string
}

/**
 * Presentation Wrapper Component
 *
 * Provides the root structure for a presentation.
 * This is a Server Component - no client-side state or hooks.
 *
 * The wrapper adds data attributes that the tracking SDK uses
 * to identify the presentation and its sections.
 *
 * @example
 * ```tsx
 * <PresentationWrapper
 *   title="ACME Corp Proposal"
 *   sections={[
 *     { id: 'hero', label: 'Introduction', order: 1, type: 'hero' },
 *     { id: 'challenge', label: 'Challenge', order: 2, type: 'content' },
 *   ]}
 * >
 *   <Hero title="Digital Transformation" />
 *   <Content id="challenge" headline="The Challenge" />
 * </PresentationWrapper>
 * ```
 */
export function PresentationWrapper({
  title,
  sections,
  children,
  className,
}: PresentationWrapperProps) {
  return (
    <div
      data-presentation-title={title}
      data-presentation-sections={sections ? JSON.stringify(sections.map(s => s.id)) : undefined}
      className={className}
    >
      {children}
    </div>
  )
}
